import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { Channel } from 'pusher-js/with-encryption';
import { combineLatest, concat, Observable, Observer, Unsubscribable } from 'rxjs';
import { debounceTime, filter, switchMap, take, tap } from 'rxjs/operators';
import { NvToastComponent } from '../../../../nvps-libraries/design/nv-toast/nv-toast.component';
import { getCurrentUser } from '../../../store/selectors/current-user-selectors';
import { ISchool } from '../../typings/interfaces/school.interface';
import { IUser } from '../../typings/interfaces/user.interface';
import { PusherService } from '../pusher/pusher-service';
import { getSchool } from './../../../store/selectors/school-selectors';
import { TValidUpdateModes } from './real-time-updates.directive';

export type TValidToastEvt = undefined | { dismissedByAction: boolean };

export type TValidUpdateModes = 'IMMEDIATE' | 'DEFERRED';

export enum ValidModes {
  IMMEDIATE = 'IMMEDIATE',
  DEFERRED = 'DEFERRED',
}

enum PusherEvents {
  FRESH_DATA_AVAILABLE = 'FRESH_DATA_AVAILABLE',
}

interface IFreshDataPayload {
  schoolId: string;
  initiator: string;
  studentIds: string[];
}

@Directive({ selector: '[realTimeUpdates]' })
export class RealTimeUpdatesDirective implements OnInit, OnDestroy {
  pusherChannel: Channel;
  realTimeSubscription: Unsubscribable;
  snackBarRef: MatSnackBarRef<NvToastComponent>;
  afterOpen$: Observable<void>;
  afterDismiss$: Observable<TValidToastEvt>;

  @Input() updateMode: TValidUpdateModes;
  @Output() freshData = new EventEmitter();

  constructor(private store: Store<any>, private pusherService: PusherService, private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.pusherChannel = this.establishPusherChannel();
    this.realTimeSubscription = this.initializeRealTime(this.pusherChannel, this.updateMode);
  }

  ngOnDestroy(): void {
    if (this.realTimeSubscription) this.realTimeSubscription.unsubscribe();
    if (this.pusherChannel) this.pusherChannel.unbind(PusherEvents.FRESH_DATA_AVAILABLE);
  }

  establishPusherChannel(): Channel {
    let pusherChannel: Channel;
    this.getCurrentSchool()
      .pipe(
        tap(({ _id: currentSchoolId }) => {
          const pusherChannelName = this.pusherService.getChannelName(currentSchoolId);
          pusherChannel = this.pusherService.pusher.subscribe(pusherChannelName);
        }),
        take(1),
      )
      .subscribe();
    return pusherChannel;
  }

  getCurrentUser(): Observable<IUser> {
    return this.store.select(getCurrentUser);
  }

  getCurrentSchool(): Observable<ISchool> {
    return this.store.select(getSchool);
  }

  initializeRealTime(channel: Channel, updateMode: TValidUpdateModes): Unsubscribable {
    return combineLatest(this.getCurrentUser(), this.connectToPusher(channel, PusherEvents.FRESH_DATA_AVAILABLE))
      .pipe(
        // this filter prevents the action taking user from receiving their own update notification
        filter(([{ _id: currentUserId }, { initiator: initiatorId }]) => initiatorId !== currentUserId),
        // prevent the user from being bombarded by toasts due to backend callbacks and BGJs
        debounceTime(10000),
        switchMap(() => {
          this.snackBarRef = this.showToast(this.updateMode);
          this.afterOpen$ = this.snackBarRef.afterOpened();
          this.afterDismiss$ = this.snackBarRef.afterDismissed();
          return concat(this.afterOpen$, this.afterDismiss$);
        }),
        tap((evt: TValidToastEvt) => this.emitModeEvent(evt, updateMode)),
      )
      .subscribe();
  }

  connectToPusher(channel: Channel, evt: string): Observable<IFreshDataPayload> {
    return Observable.create((observer: Observer<IFreshDataPayload>) =>
      channel.bind(PusherEvents[evt], (data: IFreshDataPayload) => observer.next(data)),
    );
  }

  showToast(updateMode: TValidUpdateModes): MatSnackBarRef<NvToastComponent> {
    return this.snackBar.openFromComponent(NvToastComponent, {
      data: {
        toastText: 'Students Updated',
        hasClearButton: updateMode === 'DEFERRED' ? true : false,
        actionText: updateMode === 'DEFERRED' ? 'Refresh' : null,
      },
      duration: updateMode === 'DEFERRED' ? null : 3000,
    });
  }

  emitModeEvent(evt: TValidToastEvt, updateMode: TValidUpdateModes): void {
    const isOpenEvt = !evt;
    const isDismissEvt = evt;
    const isDismissedWithAction = evt ? evt.dismissedByAction : null;
    if (isOpenEvt && updateMode === ValidModes.IMMEDIATE) this.freshData.emit();
    if (isDismissEvt && updateMode === ValidModes.DEFERRED && isDismissedWithAction) this.freshData.emit();
  }
}
